import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import scoringRuleService from '../../../api/crm/leads/scoringRuleService';
import { formatDate } from '../../../utils/stringUtils';
import { formatFieldName, formatOperatorName } from '../../../utils/stringUtils';

const ScoringRuleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rule, setRule] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) fetchRule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchRule = async () => {
    setIsLoading(true);
    try {
      const result = await scoringRuleService.getScoringRuleById(id);
      if (result.success) {
        setRule(result.rule);
      } else {
        setError(result.error || 'Failed to load scoring rule');
      }
    } catch (err) {
      setError('Failed to load scoring rule');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <div className="p-6">Loading...</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!rule) return <div className="p-6">No rule found.</div>;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate('/lead_scoring_qualification')}
        >
          Back
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold text-gray-900 break-words whitespace-normal capitalize">{rule.name}</h1>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="mb-3">
          <div className="text-sm text-gray-500">Description</div>
          <div className="text-sm text-gray-700 mt-1 break-words whitespace-normal capitalize">{rule.description || '-'}</div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-gray-500">Created</div>
            <div className="text-sm text-gray-900">{formatDate(rule.createdAt, { includeTime: false })}</div>
            <div className="text-xs text-gray-400 mt-0.5">{new Date(rule.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Modified</div>
            {rule.updatedAt ? (
              <>
                <div className="text-sm text-gray-900">{formatDate(rule.updatedAt, { includeTime: false })}</div>
                <div className="text-xs text-gray-400 mt-0.5">{new Date(rule.updatedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
              </>
            ) : (
              <div className="text-sm text-gray-900">-</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-3">Rules ({rule.rules ? rule.rules.length : 0})</h3>
        {rule.rules && rule.rules.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Field</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Operator</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Value</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Score</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rule.rules.map((r, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{formatFieldName(r.field)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{formatOperatorName(r.operator || 'equals')}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{r.value ?? r.condition ?? '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{r.score ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-sm text-gray-500">No rules defined for this scoring rule.</div>
        )}
      </div>
    </div>
  );
};

export default ScoringRuleDetail;
